import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { embedOne, formatTime } from '../../lib/utils';
import { Avatar, Button, ChatComposer, IconButton, MessageBubble, Spinner, SystemEvent } from '../../components/design-system';
import { ChatSearchBar } from '../../components/chat/ChatSearchBar';
import { HighlightedText } from '../../components/chat/HighlightedText';
import { MESSAGE_INDEX_ATTR, useChatSearch } from '../../hooks/useChatSearch';
import { ReactionMenu } from '../../components/chat/ReactionMenu';
import { useChatReactions } from '../../hooks/useChatReactions';
import { myReaction, reactionPills } from '../../lib/reactions';
import { useConversationMessages } from '../../hooks/useConversationMessages';
import { deriveHostStage, formatRange, STAGE_DOT_COLOR, STAGE_LABEL, type HostTripStage } from '../../lib/hostConversations';
import type { Message } from '../../types';
import { scrollListToBottom } from '../../lib/keyboard';

interface ChatMeta {
  conversationId: string;
  travelerId: string;
  travelerName: string | null;
  travelerAvatar: string | null;
  messages: Message[];
  stage: HostTripStage;
  dates: string | null;
  dayOf: string | null;
}

/** Who a message is from, for grouping runs of messages from the same sender. */
function senderOf(msg: Message, userId: string): 'me' | 'alma' | 'traveler' | 'system' {
  if (msg.role === 'system') return 'system';
  if (msg.sender === userId) return 'me';
  // Alma is identified by role, not by sender: messages drafted in the
  // anonymous /try chat carry no `sender`, so the traveler's own history would
  // otherwise be attributed to Alma here.
  if (msg.role === 'assistant' || msg.sender === 'alma') return 'alma';
  return 'traveler';
}

function ChatMessage({ msg, userId, lastInGroup, onReact, query, activeMatch }: {
  msg: Message;
  userId: string;
  /** Words being searched for, highlighted in the text. */
  query: string;
  /** This is the match being looked at. */
  activeMatch: boolean;
  /** The newest of a run from the same sender: Alma's avatar and name show here. */
  lastInGroup: boolean;
  onReact: (emoji: string) => void;
}) {
  if (msg.role === 'system') return <SystemEvent className="py-2">{msg.content}</SystemEvent>;

  const who = senderOf(msg, userId);
  const isMe = who === 'me';
  const isAlma = who === 'alma';

  return (
    <ReactionMenu align={isMe ? 'right' : 'left'} mine={myReaction(msg, userId)} onReact={onReact} copyText={msg.content}>
      {({ open }) => (
        <MessageBubble
          compact
          from={isMe ? 'you' : 'other'}
          // The traveler and you are the only two people here, so only Alma is labelled.
          senderName={isAlma && lastInGroup ? 'Alma' : undefined}
          avatar={isAlma && lastInGroup ? <Avatar kind="alma" size={28} /> : undefined}
          reserveGutter={isAlma && !lastInGroup}
          time={formatTime(msg.timestamp)}
          reactions={reactionPills(msg, userId)}
          onReactionsOpen={open}
        >
          {query ? <HighlightedText text={msg.content} query={query} active={activeMatch} /> : msg.content}
        </MessageBubble>
      )}
    </ReactionMenu>
  );
}

const NO_MESSAGES: Message[] = [];

export function HostChat({ onBack }: { /** On a phone, the way back to the list of chats. */ onBack?: () => void } = {}) {
  const { matchId } = useParams<{ matchId: string }>();
  const { user, profile } = useAuth();
  const [meta, setMeta] = useState<ChatMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  // Messages go out one after the other. The box is never turned off while one is on its way,
  // so the keyboard stays open and you can keep typing.
  const sendChain = useRef<Promise<void>>(Promise.resolve());
  const navigate = useNavigate();
  const bottomRef = useRef<HTMLDivElement>(null);

  function setMetaMessages(updater: (prev: Message[]) => Message[]) {
    setMeta((prev) => (prev ? { ...prev, messages: updater(prev.messages) } : prev));
  }
  const { react } = useChatReactions(meta?.conversationId, user?.id, setMetaMessages);
  const search = useChatSearch(meta?.messages ?? NO_MESSAGES);

  useEffect(() => {
    if (!user || !matchId) return;
    void (async () => {
      const { data: match } = await supabase
        .from('matches')
        .select(`
          traveler_id,
          status,
          traveler_profile:profiles!matches_traveler_id_fkey(full_name, avatar_url),
          conversation:conversations(id, messages),
          itinerary:itineraries(status, content)
        `)
        .eq('id', matchId)
        .eq('host_id', user.id)
        .maybeSingle();

      if (!match) { setLoading(false); return; }
      const raw = match as Record<string, unknown>;
      const tp = raw.traveler_profile as { full_name: string | null; avatar_url: string | null } | null;
      const convRows = raw.conversation as Array<{ id: string; messages: Message[] }> | null;
      const conv = convRows?.[0] ?? null;

      const itin = embedOne(raw.itinerary as { status: string; content: Record<string, unknown> | null } | { status: string; content: Record<string, unknown> | null }[] | null);
      const content = itin?.content ?? null;
      const start = content?.start_date ? new Date(String(content.start_date)) : null;
      const end = content?.end_date ? new Date(String(content.end_date)) : null;
      const now = new Date();
      const stage = deriveHostStage(String(raw.status ?? ''), itin?.status ?? null, start, end, now);

      let dayOf: string | null = null;
      if (stage === 'active' && start && end) {
        const total = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
        const day = Math.min(Math.floor((now.getTime() - start.getTime()) / 86400000) + 1, total);
        dayOf = `Day ${day} of ${total}`;
      }

      setMeta({
        conversationId: conv?.id ?? '',
        travelerId: (raw.traveler_id as string) ?? '',
        travelerName: tp?.full_name ?? null,
        travelerAvatar: tp?.avatar_url ?? null,
        messages: conv?.messages ?? [],
        stage,
        dates: formatRange(start, end),
        dayOf,
      });
      setLoading(false);
    })();
  }, [user, matchId]);

  useEffect(() => {
    scrollListToBottom(bottomRef.current);
  }, [meta?.messages]);

  useConversationMessages(meta?.conversationId, (messages) =>
    setMeta((prev) => (prev ? { ...prev, messages } : prev)),
  );

  function handleSend() {
    if (!input.trim() || !meta?.conversationId || !user) return;
    const content = input.trim();
    const conversationId = meta.conversationId;
    const travelerId = meta.travelerId;
    const newMsg: Message = {
      role: 'user',
      sender: user.id,
      content,
      timestamp: new Date().toISOString(),
    };
    setMeta((prev) => prev ? { ...prev, messages: [...prev.messages, newMsg] } : prev);
    setInput('');
    sendChain.current = sendChain.current.then(() => deliver(conversationId, travelerId, newMsg, content)).catch(() => {});
  }

  async function deliver(conversationId: string, travelerId: string | null | undefined, newMsg: Message, content: string) {
    const { error: rpcError } = await supabase.rpc('append_chat_message', {
      p_conv_id: conversationId,
      p_msg: newMsg,
    });
    if (rpcError) console.error('append_chat_message failed:', rpcError);

    if (travelerId) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        fetch(`${supabaseUrl}/functions/v1/send-push`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({
            user_id: travelerId,
            title: profile?.full_name?.split(' ')[0] ?? 'Host',
            body: content.slice(0, 100),
            data: { type: 'chat', match_id: matchId },
          }),
        }).catch(() => {});
      }
    }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  if (!meta?.conversationId) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-3">
        <p className="text-sm text-app-muted">Chat isn't available yet — the traveler hasn't confirmed the match.</p>
      </div>
    );
  }

  const travelerFirst = meta?.travelerName?.split(' ')[0] ?? 'Traveler';
  const stage = meta?.stage ?? 'building';
  const visibleMessages = meta!.messages;

  return (
    <div className="flex flex-col h-full">
      {/* One slim bar: who, where the trip stands, the handbook and search. */}
      <header className="shrink-0 border-b border-app-border">
        <div className="flex items-center gap-2 pl-1 pr-2 md:pl-6 md:pr-6 py-1 md:py-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              aria-label="Back to conversations"
              className="md:hidden w-11 h-11 flex items-center justify-center shrink-0 text-app-fg active:text-app-fg-2"
            >
              <i className="ti ti-chevron-left text-[length:calc(24px*var(--text-scale))]" />
            </button>
          ) : null}
          <Avatar kind="member" size={38} src={meta?.travelerAvatar ?? undefined} name={meta?.travelerName ?? 'Traveler'} />
          <div className="flex-1 min-w-0 ml-1">
            <div className="font-serif text-app-fg truncate" style={{ fontSize: 'var(--fs-body)', lineHeight: 1.15 }}>{meta?.travelerName ?? 'Traveler'}</div>
            <div className="text-app-muted text-caption mt-0.5 flex items-center gap-1.5 min-w-0">
              <span className="w-[5px] h-[5px] rounded-full shrink-0" style={{ background: STAGE_DOT_COLOR[stage] }} />
              <span className="truncate">
                {[STAGE_LABEL[stage], meta?.dates, meta?.dayOf].filter(Boolean).join(' · ')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <span className="md:hidden">
              <IconButton icon="clipboard-list" iconSize={22} label="Open handbook" onClick={() => navigate(`/host/itinerary/${matchId}`)} />
            </span>
            <span className="hidden md:inline-flex">
              <Button variant="outline" leadingIcon="clipboard-list" onClick={() => navigate(`/host/itinerary/${matchId}`)} style={{ fontSize: 'var(--fs-caption)', padding: '7px 15px' }}>
                Open handbook
              </Button>
            </span>
            <IconButton
              icon="search"
              iconSize={22}
              label="Search in conversation"
              aria-pressed={search.open}
              onClick={search.toggle}
            />
          </div>
        </div>
        {search.open && (
          <ChatSearchBar
            query={search.query}
            onQuery={search.setQuery}
            position={search.position}
            total={search.total}
            onOlder={search.older}
            onNewer={search.newer}
            onClose={search.close}
          />
        )}
      </header>

      <div className="flex-1 overflow-y-auto px-2.5 md:px-7 py-3 flex flex-col" data-keyboard-pin>
        {(!meta?.messages || meta.messages.length === 0) && (
          <div className="flex justify-center py-8">
            <p className="text-xs text-app-muted">Your conversation will appear here.</p>
          </div>
        )}
        {visibleMessages.map((msg, vi) => {
          const i = meta!.messages.indexOf(msg);
          const uid = user?.id ?? '';
          const who = senderOf(msg, uid);
          const prev = visibleMessages[vi - 1];
          const next = visibleMessages[vi + 1];
          const sameAsPrev = Boolean(prev) && who !== 'system' && senderOf(prev, uid) === who;
          const lastInGroup = !next || senderOf(next, uid) !== who;
          return (
            <div key={i} {...{ [MESSAGE_INDEX_ATTR]: i }} style={{ marginTop: vi === 0 ? 0 : sameAsPrev ? 2 : 10 }}>
              <ChatMessage
                msg={msg}
                userId={uid}
                lastInGroup={lastInGroup}
                query={search.open ? search.query : ''}
                activeMatch={search.current === i}
                onReact={(emoji) => void react(i, emoji)}
              />
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer, with the safety line always in view (one line). */}
      <div className="shrink-0 px-2.5 md:px-7 pt-1.5 pb-2 md:pb-5 pb-safe-or-2">
        <ChatComposer
          compact
          value={input}
          onChange={setInput}
          onSend={handleSend}
          placeholder={`Message ${travelerFirst}…`}
          disclaimer="Never request passport or payment info here."
        />
      </div>
    </div>
  );
}

export default HostChat;
